<script setup>
import { onMounted, ref, computed, reactive } from "vue";
import { apiQuery } from "@/api/j24api";

const loading = ref(true);
const productsArray = ref([]);
const itemArray = ref([]);
const stagedItemsArray = ref([]);
const isAdmin = ref(false);
const includeStagedItems = ref(false);

const selectedProduct = ref(null);
const selectedType = ref(null);
const selectedStagingAuthor = ref(null);

const expandedPayloads = reactive({});
const loadingPayloads = reactive({});

const objectTypeDisplayNames = {
  policies: "Policies",
  scripts: "Scripts",
  packages: "Packages",
  computergroups: "Computer Groups",
  mobiledevicegroups: "Mobile Groups",
  osxconfigurationprofiles: "macOS Configuration Profiles",
  mobiledeviceconfigurationprofiles: "Mobile Configuration Profiles",
  computerextensionattributes: "macOS Extension Attributes",
  mobiledeviceextensionattributes: "Mobile Extension Attributes",
  categories: "Categories",
  dockitems: "Dock Items",
  diskencryptionconfigurations: "Disk Encryption Configurations",
  restrictedsoftware: "Restricted Software",
  advancedcomputersearches: "Advanced Computer Searches",
  advancedmobiledevicesearches: "Advanced Mobile Searches",
  networksegments: "Network Segments",
  "/v3/computer-prestages": "Computer Prestages",
  "/v2/mobile-device-prestages": "Mobile Device Prestages",
};

const textAreaFields = ["Script_contents", "Script_contents_encoded"];

const productOptions = computed(() =>
  productsArray.value.map((p) => ({ title: p.name, value: p._id }))
);

const typeOptions = computed(() => {
  const types = new Set(allTemplates.value.map((item) => item.type));
  return Array.from(types)
    .sort()
    .map((t) => ({ title: objectTypeDisplayNames[t] || t, value: t }));
});

const stagingAuthorOptions = computed(() => {
  const authors = new Set(
    stagedItemsArray.value.map((item) => item.author).filter(Boolean)
  );
  return Array.from(authors).sort();
});

const allTemplates = computed(() => {
  if (includeStagedItems.value) {
    let staged = stagedItemsArray.value;
    if (selectedStagingAuthor.value) {
      staged = staged.filter(
        (item) => item.author === selectedStagingAuthor.value
      );
    }
    return [...itemArray.value, ...staged];
  }
  return itemArray.value;
});

const displayItems = computed(() => {
  if (selectedProduct.value) {
    const product = productsArray.value.find(
      (p) => p._id === selectedProduct.value
    );
    if (!product || !product.items) return {};

    const grouped = {};
    for (const type in product.items) {
      const names = product.items[type];
      if (!names || names.length === 0) continue;

      const matched = [];
      for (const name of names) {
        const template = allTemplates.value.find(
          (t) => t.name === name && t.type === type
        );
        if (template) {
          matched.push(template);
        } else {
          matched.push({ name, type, _id: null, missing: true });
        }
      }
      if (matched.length > 0) {
        grouped[type] = matched;
      }
    }
    return grouped;
  }

  if (selectedType.value) {
    const filtered = allTemplates.value.filter(
      (t) => t.type === selectedType.value
    );
    if (filtered.length > 0) {
      return { [selectedType.value]: filtered };
    }
    return {};
  }

  return {};
});

const hasItems = computed(() => Object.keys(displayItems.value).length > 0);

onMounted(async () => {
  await getData();
});

async function getData() {
  loading.value = true;

  let items = await apiQuery("ELA", "mdmTools/command", {
    mdm: "jamf",
    command: "getItems",
  });

  if (Array.isArray(items)) {
    items.forEach((item) => {
      item.isStaged = false;
      itemArray.value.push(item);
    });
  }

  let products = await apiQuery("ELA", "mdmTools/command", {
    mdm: "jamf",
    command: "getProducts",
  });

  if (Array.isArray(products)) {
    productsArray.value = products;
  }

  const adminResult = await apiQuery("ELA", "mdmTools/command", {
    mdm: "jamf",
    command: "isTemplateAdmin",
  });
  isAdmin.value = adminResult === true;

  if (isAdmin.value) {
    let staged = await apiQuery("ELA", "mdmTools/command", {
      mdm: "jamf",
      command: "getAllStagedItems",
    });

    if (Array.isArray(staged)) {
      staged.forEach((item) => {
        item.isStaged = true;
        stagedItemsArray.value.push(item);
      });
    }
  }

  loading.value = false;
}

function onProductChange() {
  selectedType.value = null;
  clearExpanded();
}

function onTypeChange() {
  selectedProduct.value = null;
  clearExpanded();
}

function clearExpanded() {
  for (const key in expandedPayloads) {
    delete expandedPayloads[key];
  }
  for (const key in loadingPayloads) {
    delete loadingPayloads[key];
  }
}

async function toggleExpand(item) {
  if (!item._id) return;

  const key = item._id;

  if (expandedPayloads[key]) {
    delete expandedPayloads[key];
    return;
  }

  loadingPayloads[key] = true;

  const result = await apiQuery("ELA", "mdmTools/command", {
    mdm: "jamf",
    command: "getItemPayload",
    payload: item._id,
  });

  delete loadingPayloads[key];

  if (result && result !== "Error") {
    try {
      const parsed = typeof result === "string" ? JSON.parse(result) : result;
      expandedPayloads[key] = formatPayload(parsed);
    } catch {
      expandedPayloads[key] = { Error: "Could not parse template payload" };
    }
  } else {
    expandedPayloads[key] = { Error: "Template missing from GitHub" };
  }
}

function formatPayload(obj) {
  const firstKey = Object.keys(obj)[0];
  if (!firstKey) return obj;

  const inner = obj[firstKey];
  if (typeof inner !== "object" || inner === null) return obj;

  const formatted = {};
  for (const key in inner) {
    const val = inner[key];
    if (val === null || val === undefined || val === "") continue;
    if (Array.isArray(val) && val.length === 0) continue;
    if (typeof val === "object" && !Array.isArray(val)) {
      const cleaned = removeEmpty(val);
      if (cleaned !== null) {
        formatted[capitalise(key)] = cleaned;
      }
    } else {
      formatted[capitalise(key)] = val;
    }
  }
  return formatted;
}

function removeEmpty(obj) {
  if (typeof obj !== "object" || obj === null) return obj;
  if (Array.isArray(obj)) {
    if (obj.length === 0) return null;
    return obj;
  }
  const result = {};
  let hasKeys = false;
  for (const key in obj) {
    const val = obj[key];
    if (val === null || val === undefined || val === "") continue;
    if (Array.isArray(val) && val.length === 0) continue;
    if (typeof val === "object" && !Array.isArray(val)) {
      const cleaned = removeEmpty(val);
      if (cleaned !== null) {
        result[key] = cleaned;
        hasKeys = true;
      }
    } else {
      result[key] = val;
      hasKeys = true;
    }
  }
  return hasKeys ? result : null;
}

function capitalise(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function isTextArea(key) {
  return textAreaFields.includes(key);
}

function isObject(val) {
  return typeof val === "object" && val !== null && !Array.isArray(val);
}

function formatSubValue(val) {
  if (typeof val === "object" && val !== null) {
    return Object.entries(val)
      .map(([k, v]) => `${k}: ${v}`)
      .join(", ");
  }
  return String(val);
}
</script>

<template>
  <div>
    <h2>Product Catalog</h2>
    <p class="text-medium-emphasis mb-4">
      Browse template contents by product or by type.
    </p>

    <v-progress-linear v-if="loading" indeterminate color="primary" />

    <template v-if="!loading">
      <v-row class="mb-4">
        <v-col cols="12" md="4">
          <v-select
            v-model="selectedProduct"
            :items="productOptions"
            label="Browse by Product"
            variant="outlined"
            clearable
            @update:model-value="onProductChange"
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-select
            v-model="selectedType"
            :items="typeOptions"
            label="Browse by Type"
            variant="outlined"
            clearable
            @update:model-value="onTypeChange"
          />
        </v-col>
        <v-col v-if="isAdmin" cols="12" md="4" class="d-flex align-center ga-4">
          <v-switch
            v-model="includeStagedItems"
            label="Include Staged"
            color="orange"
            hide-details
            density="compact"
          />
          <v-select
            v-if="includeStagedItems && stagingAuthorOptions.length > 0"
            v-model="selectedStagingAuthor"
            :items="stagingAuthorOptions"
            label="Staging Author"
            variant="outlined"
            clearable
            density="compact"
            hide-details
          />
        </v-col>
      </v-row>

      <div
        v-if="!selectedProduct && !selectedType"
        class="text-center text-medium-emphasis pa-8"
      >
        Select a product or type above to browse templates.
      </div>

      <div
        v-if="(selectedProduct || selectedType) && !hasItems"
        class="text-center text-medium-emphasis pa-8"
      >
        No templates found for this selection.
      </div>

      <template v-for="(items, type) in displayItems" :key="type">
        <h3 class="mt-4 mb-2">{{ objectTypeDisplayNames[type] || type }}</h3>
        <v-table density="compact" class="elevation-1 mb-4">
          <thead>
            <tr>
              <th style="width: 40px"></th>
              <th>Name</th>
              <th v-if="includeStagedItems" style="width: 120px">Status</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="item in items" :key="item._id || item.name">
              <tr
                :class="{
                  'staged-row': item.isStaged,
                  'cursor-pointer': !!item._id,
                  'text-medium-emphasis': item.missing,
                }"
                @click="toggleExpand(item)"
              >
                <td>
                  <v-icon v-if="item._id" size="small">
                    {{
                      expandedPayloads[item._id]
                        ? "mdi-chevron-down"
                        : "mdi-chevron-right"
                    }}
                  </v-icon>
                  <v-progress-circular
                    v-if="loadingPayloads[item._id]"
                    indeterminate
                    size="16"
                    width="2"
                    color="primary"
                  />
                </td>
                <td>
                  {{ item.name }}
                  <v-chip
                    v-if="item.isStaged"
                    size="x-small"
                    color="orange"
                    class="ml-2"
                  >
                    Staged
                  </v-chip>
                  <v-chip
                    v-if="item.missing"
                    size="x-small"
                    color="grey"
                    class="ml-2"
                  >
                    Not in library
                  </v-chip>
                </td>
                <td v-if="includeStagedItems">
                  <span
                    v-if="item.isStaged && item.author"
                    class="text-caption"
                  >
                    {{ item.author }}
                  </span>
                  <span v-else class="text-caption text-medium-emphasis">
                    Production
                  </span>
                </td>
              </tr>

              <!-- Expanded payload -->
              <tr v-if="expandedPayloads[item._id]">
                <td :colspan="includeStagedItems ? 3 : 2" class="pa-0">
                  <div class="pa-4 bg-grey-lighten-4">
                    <template
                      v-for="(value, key) in expandedPayloads[item._id]"
                      :key="key"
                    >
                      <template v-if="isObject(value)">
                        <div class="font-weight-bold mt-2 mb-1">{{ key }}</div>
                        <v-table density="compact" class="payload-table mb-2">
                          <tbody>
                            <tr v-for="(subVal, subKey) in value" :key="subKey">
                              <td class="payload-key">{{ subKey }}</td>
                              <td v-if="isTextArea(subKey)">
                                <textarea
                                  readonly
                                  class="payload-textarea"
                                  :value="subVal"
                                />
                              </td>
                              <td v-else-if="isObject(subVal)">
                                <div
                                  v-for="(v3, k3) in subVal"
                                  :key="k3"
                                  class="text-caption"
                                >
                                  {{ k3 }}: {{ formatSubValue(v3) }}
                                </div>
                              </td>
                              <td v-else-if="Array.isArray(subVal)">
                                <div
                                  v-for="(arrItem, idx) in subVal"
                                  :key="idx"
                                  class="text-caption"
                                >
                                  {{ formatSubValue(arrItem) }}
                                </div>
                              </td>
                              <td v-else>{{ subVal }}</td>
                            </tr>
                          </tbody>
                        </v-table>
                      </template>
                      <template v-else>
                        <v-table
                          v-if="!isObject(value)"
                          density="compact"
                          class="payload-table mb-1"
                        >
                          <tbody>
                            <tr>
                              <td class="payload-key font-weight-bold">
                                {{ key }}
                              </td>
                              <td v-if="isTextArea(key)">
                                <textarea
                                  readonly
                                  class="payload-textarea"
                                  :value="value"
                                />
                              </td>
                              <td v-else>{{ value }}</td>
                            </tr>
                          </tbody>
                        </v-table>
                      </template>
                    </template>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </v-table>
      </template>
    </template>
  </div>
</template>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
:deep(.staged-row) {
  background-color: rgba(255, 152, 0, 0.08);
}
.payload-table {
  background: transparent;
}
.payload-key {
  width: 250px;
  font-weight: 500;
  vertical-align: top;
}
.payload-textarea {
  width: 100%;
  min-height: 100px;
  resize: vertical;
  font-family: monospace;
  font-size: 12px;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: #fafafa;
}
</style>
